import {
	createContext,
	ReactElement,
	FC,
	useState,
	useCallback,
	useContext,
	useMemo
} from "react";
import {useGetLearnerAnnouncementsNotifications} from "@/app2/shared/data";

type NotificationDrawerContextValue = {
	drawerOpen: boolean;
	openDrawer: () => void;
	closeDrawer: () => void;
	notificationCount: number;
};

const initialState: NotificationDrawerContextValue = {
	drawerOpen: false,
	openDrawer: () => {},
	closeDrawer: () => {},
	notificationCount: 0
};

export const NotificationDrawerContext =
	createContext<NotificationDrawerContextValue>(initialState);

interface IProps {
	children: ReactElement;
}

/**
 * Provides notification drawer open state and unread count.
 * Count comes from improve-api GET /api/_internal/user/notifications (pageSize=1).
 */
export const NotificationDrawerProvider: FC<IProps> = ({children}) => {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const {data} = useGetLearnerAnnouncementsNotifications({page: 1, pageSize: 1});
	const notificationCount = data?.unreadTotalCount ?? 0;

	const openDrawer = useCallback(() => setDrawerOpen(true), []);
	const closeDrawer = useCallback(() => setDrawerOpen(false), []);

	const value = useMemo<NotificationDrawerContextValue>(
		() => ({
			drawerOpen,
			openDrawer,
			closeDrawer,
			notificationCount
		}),
		[drawerOpen, openDrawer, closeDrawer, notificationCount]
	);

	return (
		<NotificationDrawerContext.Provider value={value}>
			{children}
		</NotificationDrawerContext.Provider>
	);
};

export const useNotificationDrawer = (): NotificationDrawerContextValue => {
	const ctx = useContext(NotificationDrawerContext);
	if (ctx === undefined) {
		throw new Error(
			"useNotificationDrawer must be used within NotificationDrawerProvider"
		);
	}
	return ctx;
};
